<template>
  <div class="vertical-align-step-1" lang="tr">
    <h3 v-if="this.$i18n.locale === 'tr'" class="main_question text-center font-weight-normal">
      {{ $t('Neden iletişim bilgilerimi doğru girmeliyim?') }}
      <a
        id="whyContactInfo"
        data-target="#why-contact-info-modal"
        data-toggle="modal"
        href="#"
        class="btn-outline-mukellef btn-radius ml-2 mt-2"
      >
        <i class="fa fa-play link-button-icon"></i><span>{{ $t('Videoyu İzleyin') }}</span>
      </a>
    </h3>
    <form id="step1" method="POST" @submit.prevent="sendForm">
      <h3 class="main_question"><strong>1/3</strong></h3>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <h3 class="main_question">{{ $t('Kişisel Bilgileriniz') }}</h3>
            <vue-tel-input
              id="phone-mask"
              @validate="mobilePhoneValidate"
              defaultCountry="GB"
              v-model="form.mobile"
              class="form-control"
              :class="this.form.mobile && !this.mobileNumberIsValid ? 'is-invalid' : 'is-valid'"
              :input-options="{ placeholder: $t('Cep Telefonu Numarası') }"
              validCharactersOnly
            ></vue-tel-input>
            <span v-show="this.form.mobile && !this.mobileNumberIsValid" class="invalid-feedback">{{
              $t('AlertMessages.Girdiğiniz telefon numarası geçerli değil')
            }}</span>
            <error-box field="mobile" />
          </div>
        </div>
        <div class="col-md-6 order-sm-4 order-md-4">
          <h3 class="main_question">{{ $t('Doğum Bilgileriniz') }}</h3>
          <date-picker
            v-model="form.birthday"
            name="birthday"
            class="w-100"
            :class="errors.first('birthday') ? 'is-invalid' : 'is-valid'"
            input-class="date-picker input-field"
            :popup-style="{ top: '100%', left: 0 }"
            :append-to-body="false"
            format="DD/MM/YYYY"
            :disabled="isNewUser && Boolean(formCopy.birthday)"
            valueType="format"
            :lang="$i18n.locale"
            v-validate="'required|date_format:dd/MM/yyyy|ageLimit:18'"
            :error="errors.first('birthday')"
            :placeholder="$t('Doğum Tarihi (gg/aa/yyyy)')"
          ></date-picker>
          <span v-show="errors.first('birthday')" class="show-invalid invalid-feedback">{{
            errors.first('birthday')
          }}</span>
          <error-box field="birthday" />
        </div>
      </div>

      <h3 class="main_question">{{ $t('Milliyet Bilgileriniz') }}</h3>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <div class="clearfix">
              <v-select
                :placeholder="this.$t('Milliyet Secimi')"
                :options="$t('countries')"
                v-model="nationality"
                :filterBy="filterSelect"
                :disabled="isNewUser && Boolean(formCopy.nationality)"
                class="wide input-field"
                :class="{ 'is-invalid': errors.first('nationality') }"
                @input="setNationality"
                v-validate="'required'"
                name="nationality"
              >
                <span slot="no-options">{{ $t('Üzgünüz, belirtmiş olduğunuz ülke bulunamadı') }}</span>
              </v-select>
            </div>
            <span v-show="errors.first('nationality')" class="show-invalid invalid-feedback">{{
              errors.first('nationality')
            }}</span>
            <error-box field="address.nationality" />
          </div>
        </div>
      </div>

      <h3 class="main_question">{{ $t('Ülke Bilgileriniz') }}</h3>
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <div class="clearfix">
              <v-select
                :placeholder="this.$t('Ülke Secimi')"
                :options="$t('countries')"
                v-model="country"
                :filterBy="filterSelect"
                class="wide input-field"
                :disabled="isNewUser && Boolean(formCopy.address.country)"
                :class="{ 'is-invalid': errors.first('country') }"
                @input="setAddressCountry"
                v-validate="'required'"
                name="country"
              >
                <span slot="no-options">{{ $t('Üzgünüz, belirtmiş olduğunuz ülke bulunamadı') }}</span>
              </v-select>
              <span v-show="errors.first('country')" class="show-invalid invalid-feedback">{{
                errors.first('country')
              }}</span>
              <error-box field="address.country" />
            </div>
          </div>
        </div>
      </div>
      <h3 class="main_question">{{ $t('Adres Bilgileriniz') }}</h3>
      <div class="row" v-if="addressDetail">
        <div class="col-md-6">
          <div class="form-group">
            <div class="clearfix">
              <v-select
                :placeholder="$t('Adres il')"
                :options="cities"
                v-model="addressCity"
                :filterBy="filterSelect"
                :disabled="isNewUser && Boolean(formCopy.address.city_readable)"
                :searchable="false"
                class="wide input-field"
                :class="{ 'is-invalid': errors.first('address-city') }"
                @input="setAddressCity"
                v-validate="'required'"
                name="address-city"
                label="name"
                value="id"
              >
                <span slot="no-options">{{ $t('Üzgünüz, belirtmiş olduğunuz il bulunamadı') }}</span>
              </v-select>
              <span v-show="errors.first('address-city')" class="show-invalid invalid-feedback">{{
                errors.first('address-city')
              }}</span>
              <error-box field="address.city_id" />
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <div class="clearfix">
              <v-select
                v-model="addressDistrict"
                :filterBy="filterSelect"
                :options="addressDistricts"
                :searchable="false"
                :disabled="isNewUser && Boolean(formCopy.address.district_readable)"
                class="wide input-field"
                :class="{ 'is-invalid': errors.first('address-county') }"
                v-validate="'required'"
                label="name"
                name="address-county"
                :placeholder="$t('Adres ilçe')"
                value="id"
                @input="setAddressDistrict"
              >
                <span slot="no-options">{{ $t('Üzgünüz, belirtmiş olduğunuz ilçe bulunamadı') }}</span>
              </v-select>
              <span v-show="errors.first('address-city')" class="show-invalid invalid-feedback">{{
                errors.first('address-county')
              }}</span>
              <error-box field="address.district_id" />
            </div>
          </div>
        </div>
      </div>
      <div class="row mb-4">
        <div class="col-12">
          <textarea
            class="form-control input-field"
            v-validate="'required'"
            :disabled="isNewUser && Boolean(formCopy.address.fullAddress)"
            name="fullAdress"
            :class="{ 'is-invalid': errors.first('fullAdress') }"
            v-model="form.address.fullAddress"
            :placeholder="this.$t('Adresinizi Giriniz')"
            rows="4"
          ></textarea>
          <span v-show="errors.first('fullAdress')" class="show-invalid invalid-feedback">{{
            errors.first('fullAdress')
          }}</span>
        </div>
      </div>
    </form>
    <MobileVerify
      :mobile="form.mobile"
      :limit-message="limitMessage"
      ref="mobileVerifyComponent"
      @mobile-verified="emailVerified()"
    />
  </div>
</template>
<script>
export default {
  computed: {
    nationId() {
      return this.form.companies.data[0].company_nation_id;
    },
    addressCity: {
      get: function () {
        if (this.form.address.city_id) {
          return {
            id: this.form.address.city_id,
            name: this.form.address.city_readable,
          };
        }

        return null;
      },
      set: function () {},
    },
    addressDistrict: {
      get: function () {
        if (this.form.address.district_id) {
          return {
            id: this.form.address.district_id,
            name: this.form.address.district_readable,
          };
        }

        return null;
      },
      set: function () {},
    },
  },
  data() {
    return {
      country: null,
      nationality: null,
      addressDetail: false,
      birthdayToggle: false,
      cities: [],
      birthDistricts: [],
      addressDistricts: [],
      form: {
        address: {},
      },
      formCopy: {
        address: {},
      },
      mobileNumberIsValid: false,
      callback: null,
      limitMessage: null,
    };
  },
  props: {
    isNewUser: {
      type: Boolean,
    },
  },
  created() {
    this.createTestFunctions();
    this.$store.dispatch('getSelfWithCompanies').then(() => {
      const form = JSON.parse(JSON.stringify(this.$store.state.selfMeData));
      this.form = form;
      this.formCopy = JSON.parse(JSON.stringify(this.$store.state.selfMeData));
      if (this.form.mobile == null) {
        this.form.mobile = '';
      }

      if (this.form.address.country) {
        this.country = this.form.address.country;
        this.setAddressCountry(this.country);
      }
      if (this.form.nationality) {
        this.nationality = this.form.nationality;
      }
      if (this.form.nationality) {
        this.nationality = this.form.nationality;
      }
      this.form.identity_foreign_rule = true;

      if (this.form.address.city_id) {
        this.getDistricts(this.form.address.city_id, (districts) => {
          this.addressDistricts = districts;
        });
      }

      this.endWaiting();
    });

    $axios.get('city').then((s) => {
      this.cities = s.data.data;
    });
  },
  methods: {
    emailVerified() {
      this.startWaiting();
      this.callback(true);
    },
    mobilePhoneValidate(event) {
      this.mobileNumberIsValid = event.valid;
    },
    setAddressCountry(country) {
      this.form.address.country = country;
      if (['Turkey', 'Türkiye'].includes(this.form.address.country)) {
        this.addressDetail = true;
      } else {
        this.addressDetail = false;
      }
    },
    setNationality(nationality) {
      this.form.nationality = nationality;
    },
    setAddressCity(city) {
      if (!city) {
        this.form.address.city_id = null;
        this.form.address.city_readable = null;
        return;
      }

      this.form.address.city_id = city.id;
      this.form.address.city_readable = city.name;
      if (this.addressDistricts.length) {
        this.form.address.district_id = null;
        this.form.address.district_readable = null;
      }
      this.getDistricts(city.id, (districts) => {
        this.addressDistricts = districts;
      });
    },
    setAddressDistrict(district) {
      if (!district) {
        this.form.address.district_id = null;
        this.form.address.district_readable = null;

        return;
      }

      this.form.address.district_id = district.id;
      this.form.address.district_readable = district.name;
    },
    sendForm(callback) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (!this.mobileNumberIsValid) {
            window.Vue.$toasted.error(this.$t('AlertMessages.Lütfen geçerli bir telefon numarası girin'));
            return;
          }
          this.startWaiting();
          const form = JSON.parse(JSON.stringify(this.form));
          form.mobile = '+' + form.mobile.replace(/[^0-9.]/g, '');
          const birthdayGroup = form.birthday.split('/');
          if (birthdayGroup.length === 3) {
            // Change Birthday Format DD/MM/YYYYY
            form.birthday = birthdayGroup[2] + '/' + birthdayGroup[1] + '/' + birthdayGroup[0];
          }

          if (['Turkey', 'Türkiye'].includes(this.form.address.country)) {
            form.address.city_id = this.addressCity.id;
            form.address.district_id = this.addressDistrict.id;
          } else {
            form.address.city_id = null;
            form.address.city_readable = null;
            form.address.district_id = null;
            form.address.district_readable = null;
          }
          this.limitMessage = null;
          $axios
            .put('register/profile', form)
            .then((s) => {
              this.$toasted.show(this.$t('AlertMessages.Bilgileriniz kaydedildi'));
              this.callback = callback;

              if (form.mobile.startsWith('+90') && !s.data.data.is_mobile_verified) {
                this.endWaiting();
                const element = this.$refs.mobileVerifyComponent.$el;
                this.$refs.mobileVerifyComponent.resetModal();
                $(element).modal('show');
                return;
              }
              // eslint-disable-next-line node/no-callback-literal
              callback(true);
            })
            .catch((error) => {
              if (error.response && error.response.status === 429) {
                this.limitMessage = error.response.data.message;
                this.$toasted.show(this.$t('AlertMessages.Bilgileriniz kaydedildi'));
                this.callback = callback;

                if (form.mobile.startsWith('+90') && !form.is_mobile_verified) {
                  this.endWaiting();
                  const element = this.$refs.mobileVerifyComponent.$el;
                  this.$refs.mobileVerifyComponent.resetModal();
                  $(element).modal('show');
                  return;
                }

                // eslint-disable-next-line node/no-callback-literal
                callback(true);
              }
            });
        }
      });
    },
    getDistricts(cityID, callback) {
      $axios.get('district?city_id=' + cityID).then((s) => {
        callback(s.data.data);
      });
    },
    createTestFunctions() {
      this.selectGlobalCountry();
      this.selectNationality();
      this.selectCity();
      this.selectDistrict();
    },
  },
};
</script>
